import {
  ArgentoInsurancePaymentPeriodicities,
  type CoveragePlan,
  type ProductPlanSumary,
} from '~/config'
import { Coverage } from '~/layers/forms/models'
import { getMaybeRefValue } from '~/utils/getMaybeRefValue'

import type { Insurable } from './useInsurable'

import type { AsyncDataRequestStatus } from '#app'

export type CoveragePlanStatus = AsyncDataRequestStatus | 'unavailable'
export type CoveragePlanOption = {
  planData: CoveragePlan
  status: CoveragePlanStatus
}

export const useQuoter = ({
  insurable,
  brokerProductoDataOrigin,
  coveragePlan,
  periodicity,
  coverageTypeCode,
  inmediate,
}: {
  insurable: MaybeRef<Insurable>
  brokerProductoDataOrigin?: MaybeRef<SiteApiSchema<'BrokerProductoGroupByItemModel'>>
  coveragePlan?: MaybeRef<CoveragePlan | undefined | null>
  periodicity?: MaybeRef<PropsTypesOf<typeof ArgentoInsurancePaymentPeriodicities>['value']>
  coverageTypeCode?: MaybeRef<string | null>
  inmediate?: boolean
}) => {
  const { RemoteConfigurationStore, LocalConfigurationStore } = stores()

  const _insurable = computed(() => getMaybeRefValue(insurable))
  const _coveragePlan = computed(() => getMaybeRefValue(coveragePlan))
  const _coverageTypeCode = computed(() => getMaybeRefValue(coverageTypeCode))

  const brokerProductData = computed(() => {
    const _brokerProductoDataOrigin = getMaybeRefValue(brokerProductoDataOrigin)
    return {
      brokerProductoId:
        _brokerProductoDataOrigin?.brokerProductoId ??
        RemoteConfigurationStore.value?.brokerProductoId,
      productoId:
        _brokerProductoDataOrigin?.productoId ?? RemoteConfigurationStore.value?.producto?.id,
      emisorId: _brokerProductoDataOrigin?.emisorId ?? RemoteConfigurationStore.value?.emisor?.id,
      avaliablePromotionalCode: _brokerProductoDataOrigin?.aplicaDescuento ?? true,
    }
  })

  const periodicityValue = computed(() => {
    const providedValue = getMaybeRefValue(periodicity)
    return providedValue ?? Coverage.getPeriodicityList().default?.value
  })

  const parseProductPlanSumaryJson = (json?: string | null): ProductPlanSumary | undefined => {
    if (!json) return
    try {
      return JSON.parse(json)
    } catch {
      return { title: 'JSON Error', description: null }
    }
  }

  const criteria = computed(() => {
    const params: SiteApiSchema<'TarificadorMasterCriteriaModel'> = {
      brokerProductoId: brokerProductData.value.brokerProductoId,
      extraProps: [],
    }

    switch (_insurable.value.branch) {
      case 'Auto': {
        const montoAsegurado = _insurable.value.quotationParams?.initialParams.amount
        params.montoAsegurado = montoAsegurado
        params.extraProps?.push({
          monedaId: 'RD',
          valorVehiculo: montoAsegurado?.toString(),
          yearAuto: _insurable.value.quotationParams?.initialParams.year?.toString(),
          autoProps: {
            planId: _coveragePlan.value?.argentoInsuranceid,
            marcaId: _insurable.value.quotationParams?.initialParams.manufacturer,
            modelId: _insurable.value.quotationParams?.initialParams.model,
            versionId: _insurable.value.quotationParams?.finalParams.version,
            usageId: _insurable.value.quotationParams?.initialParams.use,
          },
        })
        break
      }
      case 'Viajes':
        params.extraProps?.push({
          fechaNacimientoPrincipal: _insurable.value.quotationParams?.finalParams?.insuredBirthDate
            ? _insurable.value.quotationParams?.finalParams.insuredBirthDate.toJSON()
            : undefined,
          duracion: _insurable.value.quotationParams?.initialParams?.calculatedDays
            ? _insurable.value.quotationParams?.initialParams?.calculatedDays?.toString()
            : undefined,
          viajeProps: {
            destino: _insurable.value.quotationParams?.initialParams?.destination,
            destinoId: _insurable.value.quotationParams?.initialParams?.destinationId,
            duracion: _insurable.value.quotationParams?.initialParams?.calculatedDays
              ? _insurable.value.quotationParams?.initialParams?.calculatedDays?.toString()
              : undefined,
            fechaSalida: _insurable.value.quotationParams?.initialParams.from
              ? _insurable.value.quotationParams?.initialParams.from.toJSON()
              : undefined,
            fechaRetorno: _insurable.value.quotationParams?.initialParams.to
              ? _insurable.value.quotationParams?.initialParams.to.toJSON()
              : undefined,
            planId: _coveragePlan.value?.argentoInsuranceid,
            viajeros: [],
            //_insurable.quotationParams?.finalParams?.travelers?.map(
            //  Traveler.map.toTarificadorMasterViajeViajeroExtraPropsCriteriaModel
            //),
          },
        })
        break
      default:
        if (periodicityValue.value) {
          params.extraProps?.push({
            frecuenciaPagoId:
              ArgentoInsurancePaymentPeriodicities[periodicityValue.value].id.toString(),
            frecuenciaPago:
              ArgentoInsurancePaymentPeriodicities[periodicityValue.value].text.toUpperCase(),
          })
        }
    }
    return params
  })

  const { cloned: quotations, sync: resetQuotations } = useCloned<
    Partial<Record<string, Coverage>>
  >({})

  const clearQuotation = () => {
    clear()
    resetQuotations()
  }

  const {
    execute,
    data,
    status: quotationStatus,
    clear,
  } = useFetch('/api/quoter', {
    method: 'POST',
    body: criteria,
    transform(input?: SiteApiSchema<'TarificadorMasterCoberturaModel'>) {
      if (input) return input
    },
    immediate: false,
    watch: false,
    key: brokerProductData.value.brokerProductoId?.toString(),
    timeout: 60000,
    retry: 2,
  })

  const quote = async () => {
    await execute()

    const planCode = _coveragePlan.value?.code
    if (!planCode) return

    if (quotationStatus.value === 'success') {
      const coverage = data.value?.coberturas?.[0]
      const argentoInsurancePaymentConditions = data.value?.productoCondicionPago
      const argentoInsuranceAplicableCalculatedPaymentConditions =
        data.value?.condicionesPagoCalculadas

      quotations.value[planCode] = buildCoverage({
        ...coverage,
        argentoInsurancePaymentConditions,
        argentoInsuranceAplicableCalculatedPaymentConditions,
      })
    } else {
      quotations.value[planCode] = undefined
    }
  }

  const buildCoverage = (
    quoteData?: SiteApiSchema<'TarificadorCoberturaItemModel'> & {
      argentoInsurancePaymentConditions?: SiteApiSchema<'ProductoCondicionPagoModel'>[] | null
      argentoInsuranceAplicableCalculatedPaymentConditions?:
        | SiteApiSchema<'TarificadorCondicionPagoCalculoModel'>[]
        | null
    }
  ) => {
    const {
      name: planName,
      code: planCode,
      argentoInsuranceid: argentoInsurancePlanid,
    } = _coveragePlan.value ?? {}
    const providedData: Partial<Coverage> = {
      argentoInsuranceProductId: brokerProductData.value.productoId,
      argentoInsuranceCompanyId: brokerProductData.value.emisorId,

      argentoInsurancePlanId: argentoInsurancePlanid,
      planCode,
      planName:
        planName ??
        quoteData?.details?.[0]?.sumaAsegurada?.nombre ??
        quoteData?.coberturaNombre ??
        null,
      argentoInsuranceCoverageRequestData: {
        ...criteria.value,
        selectedCoberturaCondicionAsegurabilidadIds: undefined, //TODO: To be determined
      },
      avaliablePromotionalCode: brokerProductData.value?.avaliablePromotionalCode ?? false,
    }

    const queriedData: Partial<Coverage> =
      quotationStatus.value === 'success'
        ? {
            argentoInsuranceProductName: quoteData?.cobertura?.producto,
            argentoInsuranceCompanyId: brokerProductData.value.emisorId,
            argentoInsuranceId: quoteData?.coberturaId ?? null,
            grandTotal: quoteData?.details?.[0]?.grandTotalComercial ?? null,
            argentoInsuranceInsuredAmountId: quoteData?.details?.[0]?.sumaAsegurada?.id ?? null,
            details:
              parseProductPlanSumaryJson(quoteData?.details?.[0]?.sumaAsegurada?.storeDetails) ??
              null,
            argentoInsuranceAplicablePaymentConditions:
              quoteData?.argentoInsurancePaymentConditions,
            argentoInsuranceAplicableCalculatedPaymentConditions:
              quoteData?.argentoInsuranceAplicableCalculatedPaymentConditions,
          }
        : {}
    return Coverage.load({ ...providedData, ...queriedData })
  }

  const applicableStaticCoverages = computed(() => {
    const coverage = data.value?.coberturas?.filter(
      (item) => item.cobertura?.tipoSeleccion === 'CERRADA'
    )?.[0]
    const cleanData = {
      coberturaId: coverage?.coberturaId,
      cobertura: coverage?.cobertura,
      details: coverage?.details,
      coberturaNombre: coverage?.coberturaNombre,
    }

    return cleanData.details
      ?.sort((a, b) => ((a?.sumaAsegurada?.orden ?? 0) < (b?.sumaAsegurada?.orden ?? 0) ? -1 : 1))
      .map((c) => buildCoverage({ ...cleanData, details: [c] }))
  })

  const selectedCoverage = computed(() => {
    const planCode = _coveragePlan.value?.code
    if (!planCode) return null
    return quotations.value[planCode]
  })

  const getPlanOptions = () => {
    const plansConfig = LocalConfigurationStore.value?.coveragePlanConfig
    const companyAvaliablePlans = LocalConfigurationStore.value?.associatedCompanies?.find(
      (ac) => ac.argentoInsuranceId == brokerProductData.value.emisorId
    )?.avaliablePlans

    if (!plansConfig || !companyAvaliablePlans) return []

    const getInitialStatus = (plan: CoveragePlan) => {
      if (!_insurable.value.branch) return 'unavailable'

      const planIsAvaliableForCurrentCompany =
        companyAvaliablePlans?.[
          _insurable.value.branch.toLocaleLowerCase() as Lowercase<SiteApiSchema<'RamoCobertura'>>
        ]?.[plan.code]

      return planIsAvaliableForCurrentCompany ? 'idle' : 'unavailable'
    }

    let plans =
      plansConfig?.plans?.map(
        (plan): CoveragePlanOption => ({
          planData: plan,
          status: getInitialStatus(plan),
        })
      ) ?? []

    if (_coverageTypeCode.value)
      plans = plans.filter((plan) => plan.planData.group === _coverageTypeCode.value)

    return plans
  }

  const planOptions = ref<CoveragePlanOption[]>(getPlanOptions())

  const resetPlanOptions = () => {
    planOptions.value = getPlanOptions()
  }
  if (inmediate) execute()

  return {
    quote,
    quotations,
    quotationStatus,
    criteria,
    clearQuotation,
    selectedCoverage,
    applicableStaticCoverages,
    planOptions,
    resetPlanOptions,
  }
}
